import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import { Vspeeds } from 'ff/debrief/models';
import SpeedRow from './SpeedRow';
import styled from 'styled-components';
import TableCell from '@mui/material/TableCell';
import { VspeedFormValues, SubmitHandler } from './VspeedFormValues';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import ReanalysisInProgressIndicator from 'ff/debrief/components/ReanalysisInProgressIndicator';

interface Props {
    standardSpeeds: Vspeeds;
    vSpeedOverrides: Vspeeds;
    onSubmit: SubmitHandler;
    onClose: () => void;
    isCustom?: boolean;
}

const VspeedOverrideForm = (props: React.PropsWithChildren<Props>) => {
    let fieldSchema = Yup.number()
        .typeError('Must be number')
        .min(0, 'Must be greater than zero')
        .max(1200, 'Must be less than 1200 knots')
        .integer('Must be integer');
    fieldSchema = props.isCustom
        ? fieldSchema.required()
        : fieldSchema.optional();

    const formSchema = Yup.object().shape({
        vr: fieldSchema,
        vx: fieldSchema,
        vy: fieldSchema,
        vref: fieldSchema,
        vne: fieldSchema,
        vs0: fieldSchema,
        vs1: fieldSchema,
        vsw: fieldSchema,
        vmxt: fieldSchema,
    });
    const initialValues: VspeedFormValues = {
        vr: props.vSpeedOverrides?.vr || undefined,
        vx: props.vSpeedOverrides?.vx || undefined,
        vy: props.vSpeedOverrides?.vy || undefined,
        vref: props.vSpeedOverrides?.vref || undefined,
        vne: props.vSpeedOverrides?.vne || undefined,
        vs0: props.vSpeedOverrides?.vs0 || undefined,
        vs1: props.vSpeedOverrides?.vs1 || undefined,
        vsw: props.vSpeedOverrides?.vsw || undefined,
        vmxt: props.vSpeedOverrides?.vmxt || undefined,
    };
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={props.onSubmit}
        >
            {({ isSubmitting, isValid }) => (
                <Form>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <HeaderCell sx={{ textAlign: 'start' }}>
                                        Parameter
                                    </HeaderCell>
                                    <HeaderCell sx={{ textAlign: 'end' }}>
                                        Default
                                    </HeaderCell>
                                    <HeaderCell sx={{ textAlign: 'end' }}>
                                        Override
                                    </HeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <SpeedRow
                                    name="vr"
                                    label="Vr"
                                    standard={props.standardSpeeds?.vr}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vs0"
                                    label="Vs0"
                                    standard={props.standardSpeeds?.vs0}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vs1"
                                    label="Vs1"
                                    standard={props.standardSpeeds?.vs1}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vx"
                                    label="Vx"
                                    standard={props.standardSpeeds?.vx}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vy"
                                    label="Vy"
                                    standard={props.standardSpeeds?.vy}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vsw"
                                    label="Vsw"
                                    standard={props.standardSpeeds?.vsw}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vref"
                                    label="Vref"
                                    standard={props.standardSpeeds?.vref}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vne"
                                    label="Vne"
                                    standard={props.standardSpeeds?.vne}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                                <SpeedRow
                                    name="vmxt"
                                    label="Vmxt"
                                    standard={props.standardSpeeds?.vmxt}
                                    disabled={
                                        !props.standardSpeeds && !props.isCustom
                                    }
                                />
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {props.isCustom && (
                        <Box
                            p={2}
                            borderRadius={0.5}
                            bgcolor="#EBF7FC"
                            color="#424853"
                        >
                            Please ensure that all V-speeds overrides are
                            entered for this aircraft.
                        </Box>
                    )}
                    <DialogActions>
                        <ReanalysisInProgressIndicator />
                        <Button
                            onClick={props.onClose}
                            disabled={isSubmitting}
                            variant="outlined"
                            color="info"
                            size="small"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isSubmitting || !isValid}
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="small"
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Form>
            )}
        </Formik>
    );
};

const HeaderCell = styled(TableCell)`
    font-size: 11px;
    font-weight: 700;
    background-color: #f3f4f6;
    color: #6d767d;
    text-transform: uppercase;
`;

export default VspeedOverrideForm;
